import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Paperclip, FileCheck2, FileText } from "lucide-react";

export const RECUR_LABELS = {
  none: "Única",
  daily: "Diária",
  biweekly: "A cada 15 dias",
  monthly: "Mensal",
  semiannual: "A cada 6 meses",
};

const empty = { name: "", description: "", value: "", date: new Date().toISOString().slice(0, 10), recurrence: "none", status: "pending", contract: "", cpf: "", pix_key: "", phone: "", client_id: "", client_name: "", category_id: "", category_name: "" };

export default function ExpenseDialog({ open, onOpenChange, onSaved, expense }) {
  const [form, setForm] = useState(empty);
  const [attachment, setAttachment] = useState({ path: null, name: null });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
      api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (expense) {
        setForm({
          name: expense.name, description: expense.description || "", value: expense.value,
          date: (expense.date || "").slice(0, 10), recurrence: expense.recurrence || "none", status: expense.status || "pending",
          contract: expense.contract || "", cpf: expense.cpf || "", pix_key: expense.pix_key || "", phone: expense.phone || "",
          client_id: expense.client_id || "", client_name: expense.client_name || "",
          category_id: expense.category_id || "", category_name: expense.category_name || "",
        });
        setAttachment({ path: expense.attachment_path, name: expense.attachment_name });
      } else {
        setForm(empty);
        setAttachment({ path: null, name: null });
      }
    }
  }, [open, expense]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment({ path: data.path, name: data.filename });
      toast.success("Anexo enviado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name || !form.value) return toast.error("Preencha nome e valor");
    if (!form.category_id) return toast.error("Selecione uma categoria");
    setSaving(true);
    const payload = { ...form, value: parseFloat(form.value), attachment_path: attachment.path, attachment_name: attachment.name };
    try {
      if (expense) await api.put(`/expenses/${expense.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(expense ? "Despesa atualizada" : "Despesa adicionada");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl tracking-tight">{expense ? "Editar despesa" : "Nova despesa"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Nome</Label>
            <Input data-testid="expense-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Aluguel" />
          </div>
          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea data-testid="expense-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Detalhes (opcional)" rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Valor (R$)</Label>
              <Input type="number" step="0.01" data-testid="expense-value-input" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="0,00" className="tnum" />
            </div>
            <div className="space-y-2">
              <Label>Data</Label>
              <Input type="date" data-testid="expense-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Recorrência</Label>
              <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                <SelectTrigger data-testid="expense-recurrence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(RECUR_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="expense-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">A pagar</SelectItem>
                  <SelectItem value="paid">Pago</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Categoria <span className="text-destructive">*</span></Label>
            <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v, category_name: (categories.find((c) => c.id === v) || {}).name || "" })}>
              <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="Selecione a categoria" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />{c.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Anexo (imagem ou PDF)</Label>
            <label htmlFor="attach" className="flex items-center gap-2 px-3 py-2.5 rounded-md border border-dashed border-border cursor-pointer hover:bg-accent transition-colors text-sm">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : attachment.name ? <FileCheck2 className="h-4 w-4 text-primary" /> : <Paperclip className="h-4 w-4 text-muted-foreground" />}
              <span className="truncate">{attachment.name || "Selecionar arquivo"}</span>
            </label>
            <input id="attach" type="file" accept="image/*,application/pdf" className="hidden" data-testid="expense-attachment-input" onChange={(e) => upload(e.target.files?.[0])} />
          </div>

          <div className="pt-2 border-t border-border/60">
            <div className="flex items-center gap-2 mb-3 mt-1">
              <FileText className="h-4 w-4 text-primary" aria-hidden />
              <span className="text-sm font-semibold">Dados do contrato (opcional)</span>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v, client_name: (clients.find((c) => c.id === v) || {}).name || "" })}>
                  <SelectTrigger data-testid="expense-client-select"><SelectValue placeholder="Selecione (opcional)" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contrato</Label>
                <Input data-testid="expense-contract-input" value={form.contract} onChange={(e) => setForm({ ...form, contract: e.target.value })} placeholder="Nº do contrato ou fornecedor" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>CPF / CNPJ</Label>
                  <Input data-testid="expense-cpf-input" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" />
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input data-testid="expense-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(00) 00000-0000" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Chave PIX</Label>
                <Input data-testid="expense-pix-input" value={form.pix_key} onChange={(e) => setForm({ ...form, pix_key: e.target.value })} placeholder="E-mail, telefone, CPF ou chave aleatória" />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="expense-cancel-button">Cancelar</Button>
          <Button onClick={save} disabled={saving || uploading} data-testid="expense-save-button" className="font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
